import { useMemo } from 'react';
import { useDocuments } from '../lib/documents.js';
import { formatBytes, offlineFileSet, useOfflineVault } from '../lib/offlineFiles.js';
import { tripPhase, useTrips } from '../lib/travel.js';

/**
 * The offline switch (Settings → Offline) — same chassis as the
 * Reminders card. One per-device toggle covering the whole offline set
 * (vault records + starred key documents); the status line doubles as
 * first-sync progress and the ongoing health readout.
 */
export function OfflineVaultCard() {
  const documents = useDocuments();
  const docs = documents.data ?? [];
  const trips = useTrips();
  const activeTripIds = useMemo(
    () =>
      new Set(
        (trips.data ?? [])
          .filter((t) => tripPhase(t, new Date()) !== 'past')
          .map((t) => t.id),
      ),
    [trips.data],
  );
  const vault = useOfflineVault(documents.data, activeTripIds);
  const setSize = offlineFileSet(docs, activeTripIds).length;
  const nothingToKeep = documents.isSuccess && setSize === 0 && !vault.enabled;

  let status: string;
  if (vault.progress) {
    status = `Fetching ${vault.progress.done} of ${vault.progress.total} files…`;
  } else if (nothingToKeep) {
    status = 'Nothing to keep yet — add vault records or star key documents.';
  } else if (!vault.enabled) {
    status = 'Vital records and key documents need a connection to view.';
  } else {
    const { files, bytes } = vault.summary;
    const base = `${files} ${files === 1 ? 'file' : 'files'} · ${formatBytes(bytes)} on this device`;
    status = navigator.onLine ? `${base} · ${checkedLabel(vault.lastChecked)}` : base;
  }

  return (
    <div className="reminder-toggle">
      <div className="reminder-toggle__label">
        <span className="reminder-toggle__heading">Keep the Vault on this device</span>
        <span className="reminder-toggle__status">{status}</span>
        {!vault.enabled && (
          <span className="reminder-toggle__status">
            Vault records and key documents are copied to this device and refreshed whenever
            Almanac opens online. Copies live only on this device — the app and Dropbox remain
            the real store.
          </span>
        )}
      </div>
      <div className="reminder-toggle__actions">
        {vault.enabled ? (
          <button type="button" className="btn btn--ghost btn--small" onClick={vault.disable}>
            Turn off
          </button>
        ) : (
          <button
            type="button"
            className="btn btn--primary btn--small"
            onClick={vault.enable}
            disabled={nothingToKeep}
          >
            Keep offline
          </button>
        )}
      </div>
    </div>
  );
}

function checkedLabel(iso: string | null): string {
  if (!iso) return 'checking…';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 2) return 'checked just now';
  if (mins < 60) return `checked ${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `checked ${hours}h ago`;
  return `checked ${Math.round(hours / 24)}d ago`;
}
